// The switch panel, driven by a BLE session instead of the cloud.
//
// SAME BUTTONS AS THE REAL SCREEN — literally the same RelayTile and AllBar the
// 4-touch screen draws (ui/mobile/screens/device/TouchScreen.jsx), in the same
// [0,2,1,3] physical order. That is the whole point: if the tile behaves here it
// behaves there, and a difference in behaviour is the firmware, not the UI.
//
// WHAT IT DOES NOT SHARE is TouchScreen's data. That screen fetches output
// names, schedules and button settings from the backend and follows MQTT. A
// bench device is in no account, so there is nothing to fetch and no topic to
// follow — every one of those calls would 404 or hang. Here the only source of
// truth is what the device notifies over BLE, which is also the only thing worth
// testing on this screen.
//
// ⚠️ NO OPTIMISTIC STATE, exactly as useRelayStatus refuses it. A tap sends a
// command and changes nothing; the tile recolours when the DEVICE echoes its
// status back. On a bring-up bench that is not a nicety — a button that turns
// green on tap would hide the one failure this screen exists to find.
// Unknown is BLUE, never off.

import React, { useEffect, useMemo, useState } from 'react';
import { useContentHeight } from './MobileShell';
import { Box, Typography, useTheme } from '@mui/material';
import RelayTile from '../../shared/controls/RelayTile';
import AllBar from '../../shared/controls/AllBar';
import FanGauge from '../../shared/controls/FanGauge';
import { displayOrder } from '../../../core/devices/buttonLayout';
import { deviceLabel, deviceTypeKey } from '../../../core/devices/deviceMeta';
import { VerticalSlider, PresetButton } from '../../shared/controls/LightBits';
import { WWH_PRESETS } from '../../../core/devices/light';
import {
  appMustSwap, getLightReversed, onReverseChange, swapMessage, swapWhite,
} from '../../../core/devices/lightReverse';
import {
  FAN_OFF, fanChannelsFor, fanFractionOf, fanStatusLabelOf, fanStepLabelOf, isOn,
} from '../../../core/devices/statusFormat';
import { buildAllRelays, buildRelayCommand } from '../../../core/ble/bleConfig';
import BleTypePanel, { hasBenchPanel } from './BleTypePanel';

/**
 * How many of a device's channels are fans rather than relays.
 *
 * ⚠️ DEVICEID 53 IS **NOT** A FAN ON THIS FIRMWARE, whatever its name suggests.
 * This function used to treat it as all-fan. The ESP8684 says otherwise, in the
 * only place that counts:
 *
 *   channels_for_type()      53 -> 1
 *   single_channel_dash_format()  53 reports "1-<value>" from s_switch[0] —
 *                            the BOOLEAN channel, never a speed
 *   FAN_MAP is only ever written to slot 7+fan, i.e. channel 8 or 9
 *
 * So the fan data points (DP44/45, DP105/106) exist solely on the 7+1 (type 93,
 * 8 channels) and 7+2 (type 87, 9 channels) panels, and `fanChannelsFor` — 1
 * and 2 respectively — already describes exactly that. Drawing a speed dial for
 * a type 53 would put a 0/1 value on a nine-step ladder and read as a fan stuck
 * just above off.
 *
 * (The Flutter-era fan hardware DOES take speeds on channel 1 over MQTT; that
 * is what ui/mobile/screens/device/FanScreen.jsx is for. Different generation,
 * different transport, not this bench.)
 */
function fanCountFor(deviceId) {
  return fanChannelsFor(deviceId);
}

// ⚠️ THESE FOUR NUMBERS ARE TouchScreen.jsx's, AND THAT IS THE POINT.
//
// The panel used to derive its own, smaller geometry — 8px padding, tiles capped
// at 150px, half-height All bars, `availH * 0.52` — so the bench drew a visibly
// different control from the one the customer uses. Put the two screenshots side
// by side and it reads as a different product: smaller buttons, thinner arcs,
// tighter type.
//
// A bench whose buttons are not the shipping buttons is testing something else.
// So the geometry is now TouchScreen's, formula for formula, and the room is
// made by SCROLLING the page instead of shrinking the control. See the note on
// `full` below for the one case that still shrinks.
const TILE_PAD = 10;          // TouchScreen: sx={{ p: '10px' }}
const TILE_MIN = 120;         // TouchScreen: Math.max(120, …)
const TILE_MAX = 230;         // TouchScreen: Math.min(230, …)
const STACK_GAP = '10px';     // TouchScreen: gap: '10px'

// ── WWH dimmer (DeviceID 25 / 90) over BLE ──────────────────────────────────
// ⚠️ IT IS A LIGHT, NOT A SWITCH BANK (bug, 2026-10-18: D4F98D14B0A0, type 25,
// drew as two on/off relay tiles on the BLE bench). The firmware (ESP8685
// dual_output.c) takes the app's own "w,wh" (0..255 each) from ANY transport —
// BLE goes through the same executor as MQTT — and answers "[uno]w,wh#BLE",
// which arrives here as `values`. Same sliders and presets as the cloud
// LightScreen. The sliders follow your finger while dragging, then the DEVICE's
// reply — no optimistic state, as for the relay tiles.
//
// ⚠️ REVERSED WHITE / WARM IS THE APP'S JOB ONLY WHERE THE FIRMWARE CANNOT DO IT
// (2026-10-18, revised 2026-10-23). A fixture wired the other way round carries
// DevicesInstalled.SwapWarm = 1, and the cloud screen swaps the two values on
// the wire both ways (lightReverse). Firmware that knows nothing of the flag
// needs that done here too — on every command out and every status in — or
// "White" lights warm; firmware that holds the flag itself must NOT be swapped
// for, because two swaps are the same as none.
//
// ⚠️ AND THERE IS NO BROKER HERE TO ASK. This panel runs offline by definition,
// so it reads `appMustSwap`, whose answer over Bluetooth comes from what an
// earlier online probe remembered — which is why a YES is persisted and a NO is
// not (see the note in core/devices/lightReverse).
function WwhBlePanel({ uniqueNumber, values, onCommand, disabled, color }) {
  // `reversed` here means "this app must do the exchange", not "the fixture is
  // reversed" — the caption below says so.
  const [reversed, setReversed] = useState(() => appMustSwap(uniqueNumber));
  useEffect(() => {
    if (!uniqueNumber) return undefined;
    let on = true;
    const settle = () => { if (on) setReversed(appMustSwap(uniqueNumber)); };
    getLightReversed({ UniqueNumber: uniqueNumber }).then(settle);
    const off = onReverseChange((u) => { if (String(u) === String(uniqueNumber)) settle(); });
    return () => { on = false; off(); };
  }, [uniqueNumber]);
  const wire = [Number(values?.[0]), Number(values?.[1])];
  const reported = swapWhite(wire, reversed);
  const known = reported.every((v) => Number.isFinite(v));
  const [drag, setDrag] = useState(null);   // [w, wh] while a slider is held
  useEffect(() => { setDrag(null); }, [values]);
  const shown = drag || (known ? reported : [0, 0]);
  // Every command is a "w,wh" pair — swapped onto the wire when reversed.
  const send = (cmd) => { if (!disabled && onCommand) onCommand(swapMessage(cmd, reversed)); };
  const setAt = (i, v) => {
    const next = [...shown];
    next[i] = Math.round(v);
    setDrag(next);
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
      {!known && (
        <Typography sx={{ color: `${color}99`, fontSize: 12 }}>Waiting for the light to report its level…</Typography>
      )}
      {reversed && (
        <Typography sx={{ color: `${color}99`, fontSize: 11 }}>White / warm white reversed for this light</Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
        <VerticalSlider
          label="White"
          value={shown[0]}
          activeColor="#FFFFFF"
          onChange={(v) => setAt(0, v)}
          onCommit={() => send(`${shown[0]},${shown[1]}`)}
        />
        <VerticalSlider
          label="Warm White"
          value={shown[1]}
          activeColor="#FFD08A"
          onChange={(v) => setAt(1, v)}
          onCommit={() => send(`${shown[0]},${shown[1]}`)}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        {WWH_PRESETS.map((p) => (
          <PresetButton key={p.command} label={p.label} swatch={p.swatch} onClick={() => send(p.command)} />
        ))}
      </Box>
    </Box>
  );
}

export default function BleDevicePanel({
  deviceId,
  uniqueNumber,  // for per-unit facts read from the server (WWH reverse)
  channels,
  values,        // raw numbers per channel; null/undefined = not reported yet
  onCommand,     // (command: string) => void — goes out over the open session
  disabled,
  showHeader = true,
  // Draw the control at the SAME size the customer's 4-touch screen draws it.
  // The default, because a bench button that is not the shipping button is
  // testing something else.
  full = true,
}) {
  const theme = useTheme();
  const contentH = useContentHeight();
  const c = theme.sli;

  const total = Number(channels) || 0;
  const fans = fanCountFor(deviceId);
  const switches = Math.max(0, total - fans);

  // ── Sizing — TouchScreen.jsx's arithmetic, copied exactly ─────────────────
  //
  // ⚠️ THE GRID ONLY STAYS 2×2 IF THE ROW IS WIDE ENOUGH FOR TWO TILES. The
  // tiles live in a wrapping flex row capped at `barWidth`, and each one is a
  // tile plus its padding on both sides, so the invariant is
  //
  //     barWidth  >=  2 * (tile + 2 * TILE_PAD)
  //
  // `tile * 2 + 40` against 10px padding is 2*(tile+20) exactly — it fits, with
  // nothing to spare. An earlier version used `tile * 2 + 20` against 8px
  // padding, which does not fit, and every tile wrapped onto its own row: a
  // 4-gang came out as a tall single column.
  //
  // ── Why the height budget is the FULL screen, not a slice of it ───────────
  // It used to be `innerHeight * 0.52`, on the reasoning that this panel shares
  // the display with the scan list and the exchange log. That is true and it was
  // still the wrong call: it made the bench's buttons smaller than the shipping
  // buttons, so the two screens no longer looked like the same product. The
  // panel now takes the same space TouchScreen takes and BleTestScreen scrolls,
  // which costs a swipe and keeps the control identical.
  const geom = useMemo(() => {
    const availW = window.innerWidth;
    // The content area, not the window — TouchScreen's allowance, and now the
    // same source as TouchScreen so the bench panel and the shipping panel
    // still come out identical under the shell's strip and footer.
    const availH = contentH - 64;
    const rows = switches <= 2 ? 1 : Math.ceil(switches / 2);
    // +2 for the two All bars, which are part of the stack's height budget.
    const heightDivisor = switches > 1 ? rows + 2 : rows;

    let tile = full
      ? Math.min((availW - 40) / 2, (availH - 90) / Math.max(2, heightDivisor))
      // The one caller that cannot scroll — an inline strip inside another
      // screen — still gets the compact panel. Nothing uses it today; it exists
      // so shrinking is a deliberate opt-in rather than the default it was.
      : Math.min((availW - 40) / 2, (contentH * 0.52) / Math.max(2, heightDivisor));
    tile = full
      ? Math.min(TILE_MAX, Math.max(TILE_MIN, tile))
      : Math.min(150, Math.max(72, tile));

    // Two fans have to sit side by side on a phone without wrapping, so each
    // gets a little under half the width — and a floor, because the dial stops
    // being readable below about 100px.
    const fanSize = Math.min(180, Math.max(100, (availW - 56) / (fans > 1 ? 2 : 1)));
    return {
      tile,
      iconSize: tile * 0.26,
      nameFont: Math.min(24, Math.max(14, tile * 0.13)),
      barWidth: tile * 2 + TILE_PAD * 4,
      barHeight: tile * 0.9,
      barFont: Math.min(30, Math.max(18, tile * 0.16)),
      barPad: tile * 0.12,
      fanSize,
    };
  }, [switches, fans, full, contentH]);

  const order = displayOrder(switches);
  const valueAt = (i) => (Array.isArray(values) ? values[i] : undefined);
  const known = (i) => valueAt(i) != null && Number.isFinite(Number(valueAt(i)));

  const send = (cmd) => { if (!disabled && onCommand) onCommand(cmd); };

  // ── NOT A SWITCH BANK? Then its OWN controls (2026-10-25) ───────────────
  // A Curtain Controller (13) drew relay tiles or nothing at all, because this
  // panel only ever knew switches, fans and the WWH dimmer. Everything else now
  // goes to BleTypePanel, which draws the type's real control — the same
  // component and the same rules the customer's screen uses, so the bench and
  // the product cannot behave differently. Checked BEFORE `total <= 0`: a
  // curtain's channel count says nothing about whether it has controls.
  if (hasBenchPanel(deviceId)) {
    return (
      <Box sx={{ width: '100%' }}>
        {showHeader && (
          <Typography sx={{ color: `${c.text}99`, fontSize: 11, textAlign: 'center' }}>
            {`DeviceID ${deviceId} · ${deviceLabel(deviceId)}`}
          </Typography>
        )}
        <BleTypePanel
          deviceId={deviceId}
          uniqueNumber={uniqueNumber}
          values={values}
          onCommand={onCommand}
          disabled={disabled}
          color={c.text}
          button={c.button}
        />
      </Box>
    );
  }

  if (total <= 0) return null;

  // A WWH dimmer is a light: sliders + presets, not relay tiles.
  if (deviceTypeKey(deviceId) === 'wwh') {
    return (
      <Box sx={{ width: '100%' }}>
        {showHeader && (
          <Typography sx={{ color: `${c.text}99`, fontSize: 11, textAlign: 'center' }}>
            {`DeviceID ${deviceId} · ${deviceLabel(deviceId)}`}
          </Typography>
        )}
        <WwhBlePanel uniqueNumber={uniqueNumber} values={values} onCommand={onCommand} disabled={disabled} color={c.text} />
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: STACK_GAP, py: 2,
    }}
    >
      {/* Off once a device is connected: at that point the panel IS the answer
          to "what is this", and a line restating the type is clutter above the
          only controls on screen. It moves into the ⋮ menu, where it is still
          one tap away when it matters. */}
      {showHeader && (
        <Typography sx={{ color: `${c.text}99`, fontSize: 11 }}>
          {`DeviceID ${deviceId} · ${deviceLabel(deviceId)} · ${total} channel${total === 1 ? '' : 's'}`}
        </Typography>
      )}

      {/* One relay is not an "all" — the real one-touch screen hides these too. */}
      {switches > 1 && (
        <AllBar
          variant="off"
          onClick={() => send(buildAllRelays(false))}
          width={geom.barWidth}
          height={geom.barHeight}
          fontSize={geom.barFont}
          padding={geom.barPad}
        />
      )}

      <Box sx={{
        display: 'flex', flexWrap: 'wrap', justifyContent: 'center', maxWidth: geom.barWidth,
      }}
      >
        {order.map((idx) => (
          <Box key={idx} sx={{ p: `${TILE_PAD}px` }}>
            <RelayTile
              // `S1`, not `Ch 1` — the same label the real screen shows for a
              // device whose outputs have never been renamed, which is every
              // device on a bench. A different word for the same button is a
              // difference the tester has to translate.
              name={`S${idx + 1}`}
              isOn={isOn(valueAt(idx), deviceId, idx, total)}
              isKnown={known(idx)}
              onToggle={() => send(buildRelayCommand(idx + 1, !isOn(valueAt(idx), deviceId, idx, total)))}
              // Long-press opens button settings on the real screen. There are
              // none for a device that is in no account, so it is a no-op —
              // useLongPress calls this unconditionally and would throw on null.
              onSchedule={() => {}}
              width={geom.tile}
              height={geom.tile}
              iconSize={geom.iconSize}
              nameFont={geom.nameFont}
            />
          </Box>
        ))}
      </Box>

      {switches > 1 && (
        <AllBar
          variant="on"
          onClick={() => send(buildAllRelays(true))}
          width={geom.barWidth}
          height={geom.barHeight}
          fontSize={geom.barFont}
          padding={geom.barPad}
        />
      )}

      {/* ── Fan channels — the SAME dial the fan screen draws ────────────────
          A 7+2 shows its 7 switches above and both fans here; a 7+1 shows one.
          They sit on channels 8 and 9, exactly where the firmware writes them
          (`slot = 7 + fan`).

          ⚠️ READ-ONLY, AND NOT BY CHOICE. The firmware's command parser is

              sscanf(flat, "%d-%d%c", &id, &val, &extra) == 2
                  && (val == 0 || val == 1)
                  && id >= 1 && id <= TUYA_SWITCH_CHANNELS      // 7

          — so a fan channel fails BOTH tests: the value is a duty step, not a
          bool, and the id is 8 or 9, past the switch range. `8-40` is not
          rejected with an error; it simply matches nothing and returns false.
          A draggable dial here would move under the finger and snap back with
          no explanation, which is worse than a dial that says why it cannot.
          The speeds shown are live and real — only the input is missing, and
          it is missing at the other end. */}
      {fans > 0 && (
        <Box sx={{
          display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center', mt: 0.5,
        }}
        >
          {Array.from({ length: fans }, (_, k) => switches + k).map((idx) => {
            const v = valueAt(idx);
            const isKnown = known(idx);
            return (
              <Box key={idx} sx={{ textAlign: 'center' }}>
                <Box sx={{
                  width: geom.fanSize, height: geom.fanSize, position: 'relative', mx: 'auto',
                }}
                >
                  <FanGauge
                    size={geom.fanSize}
                    frac={isKnown ? fanFractionOf(v) : 0}
                    running={isKnown && Number(v) > FAN_OFF}
                    known={isKnown}
                    stepLabel={isKnown ? fanStepLabelOf(v) : '—'}
                    statusLabel={fanStatusLabelOf(v)}
                  />
                </Box>
                <Typography sx={{ color: `${c.text}99`, fontSize: 10 }}>
                  {`Fan · ch ${idx + 1}${isKnown ? ` · ${v}` : ''}`}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {fans > 0 && (
        <Typography sx={{ color: `${c.text}66`, fontSize: 10, textAlign: 'center', px: 2 }}>
          Fan speed is reported, not settable over Bluetooth — the firmware
          accepts only 0/1 on channels 1–7.
        </Typography>
      )}
    </Box>
  );
}
